// =============================================================================
// On-Choice Modal — bidder-side lot selection (live round)
// Listens for `oc-round-start`, filtered by the viewer's bidder number, and shows
// a lightbox of candidate lots at the unit price. Taps toggle selection (keyed by
// an opaque choiceKey so custom lots work); each change is sent to the host over
// `oc-round-select` and the host's picks are mirrored back. Already-sold lots from
// earlier rounds are shown grayed + locked for context. The host runs the actual
// commit; this modal closes on `oc-round-end`.
// =============================================================================

(function () {
  'use strict';

  function whenReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  function init() {
    var modal = document.getElementById('on-choice-modal');
    if (!modal) return;
    if (!window.socket) { setTimeout(init, 250); return; }

    var socket = window.socket;
    var state = {
      roundId: null,
      unitPrice: 0,
      bidderNumber: null,
      lots: [],            // [{ choiceKey, title, imageUrl, committed }]
      chosen: {},          // choiceKey -> true
      confirmed: false,
    };

    var grid = document.getElementById('on-choice-lot-grid');
    var priceLabel = document.getElementById('on-choice-unit-price');
    var countLabel = document.getElementById('on-choice-count');
    var totalLabel = document.getElementById('on-choice-total');
    var doneBtn = document.getElementById('on-choice-done-btn');
    var cancelBtn = document.getElementById('on-choice-cancel-btn');

    function getCustomerBidderNumber() {
      if (window.MHV_LIVE_CONFIG && window.MHV_LIVE_CONFIG.customer && window.MHV_LIVE_CONFIG.customer.bidderNumber) {
        return String(window.MHV_LIVE_CONFIG.customer.bidderNumber);
      }
      if (window.currentBidderNumber) return String(window.currentBidderNumber);
      if (window.MHV_CUSTOMER && window.MHV_CUSTOMER.bidderNumber) return String(window.MHV_CUSTOMER.bidderNumber);
      return null;
    }

    function escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function chosenCount() { return Object.keys(state.chosen).length; }

    // The toast global is `MHV.toast` (mhv-dialog.js), loaded on every page by
    // theme.liquid. This file previously called `window.mhvToast`, which is assigned
    // NOWHERE in the theme — so the end-of-round message never once appeared on a
    // bidder's phone. (Q25, 2026-08-15.)
    function toast(message, type) {
      if (window.MHV && typeof MHV.toast === 'function') MHV.toast(message, type ? { type: type } : undefined);
    }

    function renderLots() {
      grid.innerHTML = '';
      state.lots.forEach(function (lot) {
        var card = document.createElement('div');
        card.className = 'on-choice-lot-card' + (lot.committed ? ' sold' : '');
        card.setAttribute('data-choice-key', String(lot.choiceKey));
        if (!lot.committed) {
          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', '0');
        }
        var img = lot.imageUrl
          ? '<img class="on-choice-lot-img" alt="" src="' + MHV.escapeAttr(lot.imageUrl) + '">'
          : '<div class="on-choice-lot-img" style="display:flex;align-items:center;justify-content:center;color:#555;font-size:11px;">No image</div>';
        var chip = lot.committed ? 'SOLD' : ('$' + state.unitPrice.toFixed(2));
        card.innerHTML =
          img +
          '<div class="on-choice-lot-price-chip">' + chip + '</div>' +
          '<div class="on-choice-lot-meta">' +
          '  <div class="on-choice-lot-title">' + escapeHtml(lot.title || ('Lot ' + (lot.lotNumber || ''))) + '</div>' +
          '</div>';
        if (!lot.committed) {
          card.addEventListener('click', function () { toggleSelect(lot.choiceKey); });
          card.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelect(lot.choiceKey); }
          });
        }
        grid.appendChild(card);
      });
      updateSelectionUi();
    }

    function updateSelectionUi() {
      var cards = grid.querySelectorAll('.on-choice-lot-card');
      cards.forEach(function (c) {
        var key = c.getAttribute('data-choice-key');
        c.classList.toggle('selected', !!state.chosen[key]);
      });
      var n = chosenCount();
      if (countLabel) countLabel.textContent = String(n);
      if (totalLabel) totalLabel.textContent = '$' + (state.unitPrice * n).toFixed(2);
      if (doneBtn) {
        doneBtn.disabled = n === 0 || state.confirmed;
        doneBtn.textContent = state.confirmed
          ? 'Waiting for the auctioneer…'
          : (n > 0 ? ('Choose for $' + (state.unitPrice * n).toFixed(2)) : 'Choose');
      }
    }

    function emitSelection() {
      if (state.roundId && state.bidderNumber && socket && socket.connected) {
        socket.emit('oc-round-select', {
          roundId: state.roundId,
          chosenKeys: Object.keys(state.chosen),
          source: 'bidder',
          bidderNumber: state.bidderNumber,
        });
      }
    }

    function toggleSelect(key) {
      if (state.confirmed) return;
      key = String(key);
      if (state.chosen[key]) delete state.chosen[key];
      else state.chosen[key] = true;
      updateSelectionUi();
      emitSelection();
    }

    function openModal() {
      modal.classList.add('open');
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }
    function closeModal() {
      modal.classList.remove('open');
      modal.style.display = 'none';
      document.body.style.overflow = '';
      state.roundId = null;
      state.lots = [];
      state.chosen = {};
      state.confirmed = false;
    }

    function confirmChoice() {
      if (chosenCount() === 0 || state.confirmed) return;
      state.confirmed = true;
      emitSelection();
      updateSelectionUi();
    }

    // ---- socket wiring ----
    socket.on('oc-round-start', function (data) {
      if (!data || !data.roundId || !Array.isArray(data.lots)) return;
      var me = getCustomerBidderNumber();
      if (!me) return;
      if (String(data.bidderNumber) !== me) return; // not this viewer's choice

      state.roundId = data.roundId;
      state.unitPrice = parseFloat(data.unitPrice) || 0;
      state.bidderNumber = me;
      state.confirmed = false;
      state.lots = data.lots.map(function (l) {
        return {
          choiceKey: l.choiceKey,
          title: l.title || ('Lot ' + (l.lotNumber || '')),
          lotNumber: l.lotNumber || '',
          imageUrl: l.imageUrl || (l.product && l.product.images && l.product.images[0] && (l.product.images[0].url || l.product.images[0].src)) || null,
          committed: !!l.committed,
        };
      });
      state.chosen = {};
      if (priceLabel) priceLabel.textContent = '$' + state.unitPrice.toFixed(2);
      renderLots();
      openModal();
    });

    socket.on('oc-round-select', function (data) {
      if (!data || data.roundId !== state.roundId) return;
      // Apply the host's (or scribe's) picks. Ignore echoes of our own taps so we
      // don't fight our optimistic UI.
      if (data.source === 'bidder') return;

      // Grey out anything that has actually sold, so a lot that went in an earlier round
      // can't be picked again. Sold lots are also dropped from our picks — keeping one
      // selected would let the bidder "choose" a lot that is already gone. (Q25.)
      var sold = {};
      (data.committedKeys || []).forEach(function (k) { sold[String(k)] = true; });
      var newlySold = false;
      state.lots.forEach(function (lot) {
        if (sold[String(lot.choiceKey)] && !lot.committed) { lot.committed = true; newlySold = true; }
      });

      var next = {};
      (data.chosenKeys || []).forEach(function (k) {
        k = String(k);
        if (!sold[k]) next[k] = true;
      });
      state.chosen = next;

      // A partial-failure retry reopens the round. Without this the bidder is frozen at
      // "Waiting for the auctioneer…" — `confirmed` disables the button AND makes
      // toggleSelect() a no-op — while the host is being told to have them pick again.
      if (data.reopen && state.confirmed) {
        state.confirmed = false;
        toast('Some lots could not be sold — please choose again.', 'warning');
      }

      // renderLots repaints the SOLD chips and detaches their click handlers; the cheap
      // updateSelectionUi only re-toggles the `selected` class.
      if (newlySold) renderLots();
      else updateSelectionUi();
    });

    socket.on('oc-round-end', function (data) {
      if (!data) return;
      if (state.roundId && data.roundId && data.roundId !== state.roundId) return;
      toast(data.cleared ? 'The auctioneer closed this choice.' : 'Sale recorded. Thanks!',
        data.cleared ? undefined : 'success');
      closeModal();
    });

    // ---- buttons ----
    if (doneBtn) doneBtn.addEventListener('click', confirmChoice);
    if (cancelBtn) cancelBtn.addEventListener('click', async function () {
      if (await MHV.confirm('Clear your selections?', { title: 'Clear Selections' })) {
        state.chosen = {};
        state.confirmed = false;
        updateSelectionUi();
        emitSelection();
      }
    });

    // Escape clears selections (does not close — the auctioneer owns the round).
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!modal.classList.contains('open')) return;
      if (cancelBtn) cancelBtn.click();
    });
  }

  whenReady(init);
})();
